import { ComponentType } from "@angular/cdk/portal";
import { Injectable } from "@angular/core";
import { Animation, AnimationController, ModalController, Platform } from "@ionic/angular";
import { CreateModalOptions, ModalAlign, ModalMode } from "src/app/interfaces/modal";
import { GeneralService } from 'src/app/services/general/general.service';

@Injectable({
  providedIn: "root"
})
export class ModalService {
  constructor(
    private modalController: ModalController,
    private platform: Platform,
    private animationCtrl: AnimationController,
    private generalService: GeneralService
  ) {}

  openBottomSheet(component: ComponentType<unknown>, data?: any, backdropDismiss = true, cssClass?: string): Promise<HTMLIonModalElement> {
    return this.createModal({
      component: component,
      cssClass: ['custom-bottom-modal', cssClass ? cssClass : ''],
      align: ModalAlign.BOTTOM,
      backdropDismiss,
      componentProps: data ? {...data} : undefined
    })
  }

  openDialog(component: ComponentType<unknown>, data?: any, cssClass?: string): Promise<HTMLIonModalElement> {
    return this.createModal({
      component,
      cssClass: ['custom-center-modal', 'dialog', cssClass ? cssClass : ''],
      componentProps: data ? {...data} : undefined,
      enterAnimation: (baseEl: any) => this.dialogEnterAnimation(baseEl),
      leaveAnimation: (baseEl: any) => this.dialogLeaveAnimation(baseEl)
    })
  }

  openExternalLink(link: string): void {
    this.generalService.openInappBrowser(link);
  }

  openMenuDialog(component: ComponentType<unknown>, data?: any): Promise<HTMLIonModalElement> {
    return this.createModal({
      component,
      cssClass: ['custom-center-modal', 'menu-dialog'],
      componentProps: data ? {...data} : undefined,
      enterAnimation: (baseEl: any) => this.dialogEnterAnimation(baseEl),
      leaveAnimation: (baseEl: any) => this.dialogLeaveAnimation(baseEl)
    })
  }

  createModal(options: CreateModalOptions): Promise<HTMLIonModalElement> {
    return this.modalController.create({
      ...options,
      cssClass: options.cssClass ? [...options.cssClass] : (
        options.align === ModalAlign.FULL ? 'custom-full-modal' :
        options.align === ModalAlign.BOTTOM ? 'custom-bottom-modal' : 'custom-center-modal'
      ),
      mode: this.platform.is('android') ? ModalMode.android : ModalMode.ios,
      swipeToClose: options.swipeToClose ?? false
    });
  }

  async dismiss(returnValue?: any): Promise<boolean> {
    const topModal = await this.modalController.getTop();
    if (topModal !== undefined && topModal !== null) {
      return await this.modalController.dismiss(returnValue);
    } else {
      return false;
    }
  }

  async dismissAll(): Promise<void> {
    const topModal = await this.modalController.getTop();
    if (topModal === undefined) {
      return;
    }
    topModal.dismiss();
    topModal.onDidDismiss().then(async () => {
      if (await this.modalController.getTop() !== undefined) {
        this.dismissAll();
      }
    });
  }

  /**
   * 제일 상단에 위치한 모달 종료.
   * 만약 모달 없었다면 false, 아니면 true
   */
  async dismissTopModal(): Promise<boolean> {
    const topModal = await this.modalController.getTop();
    if (topModal === undefined) {
      return false;
    } else {
      await topModal.dismiss();
      return true;
    }
  }

  private dialogEnterAnimation(baseEl: any): Animation {
    const backdropAnimation = this.animationCtrl.create()
      .addElement(baseEl.querySelector('ion-backdrop')!)
      .fromTo('opacity', '0.01', '0.25');

    const wrapperAnimation = this.animationCtrl.create()
      .addElement(baseEl.querySelector('.modal-wrapper')!)
      .keyframes([
        { offset: 0, opacity: '0', transform: 'scale(0.8)' },
        { offset: 1, opacity: '0.99', transform: 'scale(1)' }
      ]);

    return this.animationCtrl.create()
      .addElement(baseEl)
      .easing('ease')
      .duration(150)
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }

  private dialogLeaveAnimation(baseEl: any): Animation {
    return this.dialogEnterAnimation(baseEl)
      .duration(100)
      .direction('reverse');
  }
}
